/**
 * ETA-671 Apprenticeship Application + Tuition Agreement
 *
 * Mandatory post-registration onboarding page. Collects the apprentice's
 * pertinent information (name, address, SSN/State ID, DOB, phone), requires
 * explicit acknowledgement of the tuition contract + factoring authorization
 * + hold-harmless clause + student guarantee, and captures a typed
 * electronic signature. On submit it POSTs /api/apprenticeship/agreement/sign
 * and routes the user back into the portal.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

const PROGRAM_OPTIONS = [
  { code: 'cna-prep', title: 'Certified Medical Assistant (CMA) Training Program — Online', stated_tuition: 2500 },
  { code: 'lpn-prep', title: 'Practical Nurse Tech Apprenticeship Program — Online', stated_tuition: 8500 },
  { code: 'phlebotomy-prep', title: 'Phlebotomy Technician — Online', stated_tuition: 2500 },
  { code: 'emt-prep', title: 'Emergency Medical Technician (EMT) — Online', stated_tuition: 2500 },
  { code: 'homeland-security-prep', title: 'Master Homeland Security Specialist Training Program — Online', stated_tuition: 2500 },
  { code: 'medical-terminology-prep', title: 'Medical Terminology — Online', stated_tuition: 2500 },
  { code: 'medical-aide-prep', title: 'Medical Aide — Outpatient Clinic Apprenticeship', stated_tuition: 2500 },
  { code: 'ged-prep', title: 'GED Adult Learning — Online', stated_tuition: 99 },
  // Feb 2026 additions
  { code: 'cybersecurity-prep', title: 'Cyber Security — Online', stated_tuition: 99 },
  { code: 'forklift-osha-prep', title: 'Forklift OSHA Certification — Online', stated_tuition: 99 },
  { code: 'medical-billing-coding-prep', title: 'Medical Billing & Coding — Online', stated_tuition: 2500 },
  { code: 'doctor-natural-health', title: 'Doctor of Natural Health — Online (Doctorate)', stated_tuition: 2500 },
  { code: 'doctor-holistic-wellness', title: 'Doctor of Holistic Wellness — Online (Doctorate)', stated_tuition: 2500 },
  { code: 'herbal-health-practitioner', title: 'Herbal Health Practitioner Certification — Online (Doctorate)', stated_tuition: 2500 },
  { code: 'integrative-wellness-consultant', title: 'Integrative Wellness Consultant — Online (Doctorate)', stated_tuition: 2500 },
  { code: 'doctor-vital-health-sciences', title: 'Doctor of Vital Health Sciences — Practitioner — Online (Doctorate)', stated_tuition: 2500 },
];

// Per-course pricing model (must match backend/routers/online_courses.py).
//  • Flat $99 programs (GED, Cyber Security, Forklift OSHA): single fee, no exams.
//  • All other paid programs: $199 enrollment + $149 mid-term + $149 final = $497 collected.
// is_flat = true ⇒ no mid-term/final exam fees.
const FLAT_99_PROGRAMS = new Set(['ged-prep', 'cybersecurity-prep', 'forklift-osha-prep']);

const getProgramPricing = (code) => {
  if (FLAT_99_PROGRAMS.has(code)) {
    return {
      is_flat: true,
      enrollment: 99,
      midterm: 0,
      final_exam: 0,
      total_collected: 99,
      breakdown: 'Single flat $99 — no exams, no deferred tuition.',
      enrollment_label: '$99',
    };
  }
  return {
    is_flat: false,
    enrollment: 199,
    midterm: 149,
    final_exam: 149,
    total_collected: 497,
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected. Remainder is the Deferred Tuition payable only on material default of this Agreement.',
    enrollment_label: '$199',
  };
};

const PROGRAM_CODE_FROM_ENUM = {
  ONLINE_CNA_PREP: 'cna-prep',
  ONLINE_LPN_PREP: 'lpn-prep',
  ONLINE_PHLEBOTOMY_PREP: 'phlebotomy-prep',
  ONLINE_EMT_PREP: 'emt-prep',
  ONLINE_HOMELAND_PREP: 'homeland-security-prep',
  ONLINE_MED_TERM_PREP: 'medical-terminology-prep',
  ONLINE_MEDICAL_AIDE_PREP: 'medical-aide-prep',
  ONLINE_GED_PREP: 'ged-prep',
  // Feb 2026 additions
  ONLINE_CYBERSECURITY_PREP: 'cybersecurity-prep',
  ONLINE_FORKLIFT_OSHA: 'forklift-osha-prep',
  ONLINE_MEDICAL_BILLING_CODING: 'medical-billing-coding-prep',
  ONLINE_DOCTOR_NATURAL_HEALTH: 'doctor-natural-health',
  ONLINE_DOCTOR_HOLISTIC_WELLNESS: 'doctor-holistic-wellness',
  ONLINE_HERBAL_HEALTH_PRACTITIONER: 'herbal-health-practitioner',
  ONLINE_INTEGRATIVE_WELLNESS_CONSULTANT: 'integrative-wellness-consultant',
  ONLINE_DOCTOR_VITAL_HEALTH_SCIENCES: 'doctor-vital-health-sciences',
};

const resolveProgramCode = (user) => {
  if (!user) return 'cna-prep';
  const raw = user.program || '';
  return PROGRAM_CODE_FROM_ENUM[raw] || raw.toLowerCase() || 'cna-prep';
};

const ApprenticeshipAgreement = ({ user, onAgreementSigned }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('next') || '/online-courses';

  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: 'TN',
    zip_code: '',
    ssn_or_state_id: '',
    date_of_birth: '',
    phone: user?.phone || '',
    email: user?.email || '',
    program_code: resolveProgramCode(user),
    typed_signature: '',
  });
  const [ack, setAck] = useState({
    agree_to_terms: false,
    agree_to_tuition_contract: false,
    agree_to_factoring: false,
    agree_to_hold_harmless: false,
    agree_to_promise_to_pay: false,
    agree_to_survival: false,
  });
  const [autopay, setAutopay] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // If already signed, shortcut out.
    if (!user?.id) return;
    api
      .get(`/apprenticeship/agreement/${user.id}/status`)
      .then((r) => {
        if (r.data?.agreement_signed) {
          onAgreementSigned && onAgreementSigned();
          navigate(redirectTo, { replace: true });
        }
      })
      .catch(() => {});
  }, [user, navigate, redirectTo, onAgreementSigned]);

  const program = PROGRAM_OPTIONS.find((p) => p.code === form.program_code) || PROGRAM_OPTIONS[0];
  const pricing = getProgramPricing(form.program_code);
  const allAck = Object.values(ack).every(Boolean);
  const signatureMatchesName =
    form.typed_signature.trim().toLowerCase() === form.full_name.trim().toLowerCase() &&
    form.typed_signature.trim().length > 1;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const toggleAck = (key) => setAck({ ...ack, [key]: !ack[key] });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allAck) {
      setError('You must acknowledge all six contract sections to continue.');
      return;
    }
    if (!signatureMatchesName) {
      setError('Your typed signature must exactly match the full legal name above.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/apprenticeship/agreement/sign', {
        user_id: user.id,
        ...form,
        ...ack,
      });

      // Autopay nudge: if the student opted in (only available for non-flat
      // programs), immediately spin up a Stripe checkout session for the
      // program enrollment fee and forward them to the hosted checkout. After
      // Stripe redirects back, the existing /online-courses success flow will
      // mark the enrollment paid.
      if (autopay && !pricing.is_flat) {
        try {
          const origin = window.location.origin;
          const r = await api.post('/online-courses/enroll', {
            course_id: form.program_code,
            // After Stripe success, land directly inside the modules viewer with
            // ?welcome=1 so the celebration modal fires on first arrival.
            success_url: `${origin}/online-course/${form.program_code}?welcome=1`,
            cancel_url: `${origin}/online-courses?canceled=1`,
          });
          if (r.data?.checkout_url) {
            onAgreementSigned && onAgreementSigned();
            window.location.href = r.data.checkout_url;
            return;
          }
        } catch (payErr) {
          // Fall through to the standard flow — agreement is signed; student
          // can still pay later from the Online Courses page.
          console.warn('Autopay redirect failed', payErr);
        }
      }

      onAgreementSigned && onAgreementSigned();
      // Use a full-page navigation so the route guard re-evaluates with the
      // freshly-persisted `user.agreement_signed = true` from localStorage.
      // (A SPA `navigate(...)` here hits the gate while React state is still
      // stale, which bounces the student back to the agreement page.)
      window.location.href = redirectTo;
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to submit agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page" data-testid="apprenticeship-agreement-page" style={{ minHeight: '100vh', padding: '24px 16px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div className="auth-card" style={{ padding: '28px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <img src="/usdol_seal.png" alt="USDOL Seal" style={{ width: 56, height: 56 }} />
            <div>
              <div style={{ fontSize: '0.72rem', letterSpacing: 0.8, color: '#64748b', textTransform: 'uppercase' }}>
                U.S. Department of Labor · ETA Form 671
              </div>
              <h1 style={{ margin: 0, fontSize: '1.45rem', color: '#0b2545' }}>
                Apprenticeship Application &amp; Tuition Agreement
              </h1>
            </div>
          </div>

          <div
            data-testid="agreement-disclaimer"
            style={{
              background: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: 10,
              padding: '12px 16px',
              fontSize: '0.82rem',
              color: '#065f46',
              lineHeight: 1.5,
              marginBottom: 18,
            }}
          >
            <strong>USDOL Registered Apprenticeship:</strong> Every program is administered
            under the U.S. Department of Labor Registered Apprenticeship framework by
            MedCare Nursing Institute &amp; Training Academy Inc. through the Office of
            Workforce Development.
          </div>

          <p style={{ color: '#475569', fontSize: '0.9rem', marginTop: 0 }}>
            You must complete this Apprenticeship Application and electronically sign the
            Tuition Agreement before you can access any course content. The information you
            provide is used solely for program administration.
          </p>

          {error && (
            <div className="alert alert-error" data-testid="agreement-error" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="apprenticeship-agreement-form">
            {/* Section I: Apprentice Info */}
            <h3 style={{ color: '#0b2545', fontSize: '1rem', marginTop: 6 }}>Section I — Apprentice Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Full Legal Name</label>
                <input className="form-input" name="full_name" required data-testid="apprentice-name-input"
                  value={form.full_name} onChange={handleChange} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Street Address</label>
                <input className="form-input" name="address_line1" required data-testid="apprentice-address1-input"
                  value={form.address_line1} onChange={handleChange} placeholder="123 Main St" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Address Line 2 <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>(optional)</span></label>
                <input className="form-input" name="address_line2" data-testid="apprentice-address2-input"
                  value={form.address_line2} onChange={handleChange} placeholder="Apt / Suite" />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" name="city" required data-testid="apprentice-city-input"
                  value={form.city} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">State (2-letter)</label>
                <input className="form-input" name="state" required maxLength={2} data-testid="apprentice-state-input"
                  value={form.state} onChange={handleChange} style={{ textTransform: 'uppercase' }} />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP Code</label>
                <input className="form-input" name="zip_code" required data-testid="apprentice-zip-input"
                  value={form.zip_code} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-input" name="phone" required data-testid="apprentice-phone-input"
                  value={form.phone} onChange={handleChange} placeholder="(901) 555-0123" />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input className="form-input" name="date_of_birth" type="date" required data-testid="apprentice-dob-input"
                  value={form.date_of_birth} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">SSN <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>or State ID Number</span></label>
                <input className="form-input" name="ssn_or_state_id" required data-testid="apprentice-ssn-input"
                  value={form.ssn_or_state_id} onChange={handleChange} placeholder="123-45-6789" />
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
                  Encrypted in transit. Masked on the generated PDF (only the last 4 digits display).
                </p>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Apprenticeship Program</label>
                <select className="form-input" name="program_code" value={form.program_code} onChange={handleChange}
                  data-testid="apprentice-program-select" required>
                  {PROGRAM_OPTIONS.map((p) => (
                    <option key={p.code} value={p.code}>{p.title}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Section II: Tuition Agreement */}
            <h3 style={{ color: '#0b2545', fontSize: '1rem', marginTop: 18 }}>Section II — Tuition Agreement</h3>
            <div
              data-testid="tuition-summary"
              style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 10,
                padding: '14px 16px',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Stated Tuition Value</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0b2545' }}>
                    ${program.stated_tuition.toLocaleString()}.00
                  </div>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#1e3a8a', maxWidth: 460, lineHeight: 1.5 }}>
                  This is the USDOL-reported stated tuition value for the <strong>{program.title}</strong>.
                  {' '}
                  {pricing.is_flat ? (
                    <>You pay a <strong>single flat {pricing.enrollment_label} fee</strong>. There are no mid-term or final exams and no deferred tuition.</>
                  ) : (
                    <>You pay <strong>${pricing.enrollment} program enrollment</strong> + <strong>${pricing.midterm} mid-term exam</strong> + <strong>${pricing.final_exam} final exam</strong> = <strong>${pricing.total_collected} collected</strong>. The remainder is the <strong>Deferred Tuition</strong> that becomes payable only upon your material default of this Agreement.</>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              <AckRow
                testId="ack-terms"
                checked={ack.agree_to_terms}
                onToggle={() => toggleAck('agree_to_terms')}
                title="I have read and agree to the Apprenticeship Agreement."
                body="I understand this is a U.S. Department of Labor (USDOL) Registered Apprenticeship Program administered by MedCare Nursing Institute & Training Academy Inc. through the Office of Workforce Development."
              />
              <AckRow
                testId="ack-tuition"
                checked={ack.agree_to_tuition_contract}
                onToggle={() => toggleAck('agree_to_tuition_contract')}
                title="I agree to the Tuition Agreement and authorize MedCare to fund or request funding on my behalf."
                body="I authorize MedCare and its authorized agents, successors, and assigns to fund the Deferred Tuition and/or request funding from any grant, scholarship, workforce, employer-sponsorship, or third-party financing source, and to collect the Deferred Tuition (plus reasonable costs) upon my material default."
              />
              <AckRow
                testId="ack-factoring"
                checked={ack.agree_to_factoring}
                onToggle={() => toggleAck('agree_to_factoring')}
                title="I authorize MedCare to sell, assign, or factor this Tuition Agreement."
                body="I expressly authorize MedCare to sell, assign, factor, or otherwise transfer this Tuition Agreement and any resulting receivable to one or more financing partners or factoring companies, who shall succeed to all rights hereunder."
              />
              <AckRow
                testId="ack-hold-harmless"
                checked={ack.agree_to_hold_harmless}
                onToggle={() => toggleAck('agree_to_hold_harmless')}
                title="I agree to indemnify and hold MedCare and its agents harmless."
                body="I agree to indemnify, defend, and hold harmless MedCare, its officers, directors, employees, instructors, clinical placement partners, financing partners, assignees, and agents from any and all claims, damages, losses, liabilities, and expenses arising out of my participation in the program or breach of this Agreement."
              />
              <AckRow
                testId="ack-promise-to-pay"
                checked={ack.agree_to_promise_to_pay}
                onToggle={() => toggleAck('agree_to_promise_to_pay')}
                title="I unconditionally PROMISE TO PAY the Stated Tuition to MedCare or any assignee."
                body="I unconditionally promise and agree to pay the full Stated Tuition above — plus accrued interest, late charges, reasonable collection costs, and attorneys' fees — to MedCare or to any assignee, factor, or holder in due course of this Agreement. This promise is absolute, unconditional, and independent of any claim, defense, offset, or counterclaim I may have."
              />
              <AckRow
                testId="ack-survival"
                checked={ack.agree_to_survival}
                onToggle={() => toggleAck('agree_to_survival')}
                title="I AGREE AND CONSENT that my debt & covenants SURVIVE termination, withdrawal, default, or assignment."
                body="My payment obligations and all covenants in Sections II and IIA of this Agreement shall SURVIVE termination, cancellation, or expiration of this Agreement for any reason, my withdrawal or dismissal from the program, my material default, any assignment or factoring of this Agreement, and the issuance or denial of any certificate of completion. I further waive any defense of lack of consideration, failure of consideration, or assignment-based defense."
              />
            </div>

            {/* Student Guarantee Callout */}
            <div
              data-testid="student-guarantee"
              style={{
                marginTop: 14,
                background: '#ecfdf5',
                border: '1px solid #bbf7d0',
                borderRadius: 10,
                padding: '14px 16px',
                fontSize: '0.85rem',
                color: '#065f46',
                lineHeight: 1.55,
              }}
            >
              <strong>Student Guarantee.</strong> MedCare guarantees that, upon successful completion
              of all program modules (and, where applicable, admin-verified clinical hours),
              MedCare will issue a Certificate of Completion (with appended transcript) within <strong>30 days</strong>.
              If we fail to do so due to MedCare&apos;s sole fault, the <strong>{pricing.enrollment_label} program-enrollment fee</strong> paid will be refunded.
              If you complete the program but fail an occupational placement assessment, MedCare will
              provide <strong>one (1) complimentary retake</strong> of the program within 12 months.
            </div>

            {/* Section V: Signature */}
            <h3 style={{ color: '#0b2545', fontSize: '1rem', marginTop: 18 }}>Section V — Electronic Signature</h3>
            <p style={{ fontSize: '0.82rem', color: '#475569', marginTop: 0 }}>
              Type your full legal name exactly as entered above. This, together with your
              date-time stamp and IP address, constitutes a binding electronic signature under
              the federal E-SIGN Act and Tennessee UETA.
            </p>
            <div className="form-group">
              <label className="form-label">Typed Signature</label>
              <input
                className="form-input"
                name="typed_signature"
                required
                data-testid="apprentice-signature-input"
                value={form.typed_signature}
                onChange={handleChange}
                placeholder="Type your full legal name exactly"
                style={{
                  fontFamily: '"Brush Script MT", "Segoe Script", cursive',
                  fontSize: '1.3rem',
                  letterSpacing: 0.5,
                }}
              />
              {!signatureMatchesName && form.typed_signature.length > 0 && (
                <p style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: 4 }}>
                  Signature must match your full legal name exactly.
                </p>
              )}
            </div>

            {/* Autopay nudge — single optional checkbox */}
            {!pricing.is_flat && (
              <label
                data-testid="autopay-nudge"
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginTop: 14,
                  padding: '14px 16px',
                  background: autopay ? 'linear-gradient(135deg,#ecfdf5 0%,#fef3c7 100%)' : '#f8fafc',
                  border: `2px solid ${autopay ? '#10b981' : '#e2e8f0'}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                <input
                  type="checkbox"
                  checked={autopay}
                  onChange={() => setAutopay(!autopay)}
                  data-testid="autopay-nudge-input"
                  style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#065f46' }}>
                    Auto-charge my {pricing.enrollment_label} program-enrollment fee right now via Stripe
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
                    Get instant access to all your course modules. Mid-term (<strong>${pricing.midterm}</strong>) and final exam (<strong>${pricing.final_exam}</strong>) are paid separately as each one unlocks. The remaining stated tuition stays deferred under your
                    signed ETA-671 Agreement and is only payable on material default. You can also
                    uncheck this and pay later from the Online Courses page.
                  </div>
                </div>
              </label>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 12 }}
              disabled={submitting || !allAck || !signatureMatchesName}
              data-testid="apprentice-sign-btn"
            >
              {submitting
                ? 'Submitting…'
                : autopay && !pricing.is_flat
                ? `Sign & Pay ${pricing.enrollment_label} to Get Instant Access`
                : 'Sign & Submit Apprenticeship Agreement'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const AckRow = ({ testId, checked, onToggle, title, body }) => (
  <label
    data-testid={testId}
    style={{
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      padding: '10px 12px',
      background: checked ? '#ecfeff' : '#f8fafc',
      border: `1px solid ${checked ? '#67e8f9' : '#e2e8f0'}`,
      borderRadius: 8,
      cursor: 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      style={{ marginTop: 4 }}
      data-testid={`${testId}-input`}
    />
    <div>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0b2545' }}>{title}</div>
      <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 3, lineHeight: 1.45 }}>{body}</div>
    </div>
  </label>
);

export default ApprenticeshipAgreement;
